/**
 * Every literal used by the Fusion Assistant deep-link buttons lives here so
 * there is exactly one place to change when the assistant moves.
 */
import { getEnvironment } from '../authentication/config';

/**
 * AgentSpaces launch URL for the Fusion Assistant, minus the initialPrompt
 * query parameter.
 *
 * The agent name (`fusion-assistant`), its owning AIM package
 * (`FusionLocalAgents`) and the blueprint are pinned here and are the same
 * values used by the "Edit in AgentSpaces" browser userscript that adds an
 * assistant entry point to the published documentation pages. If the agent is
 * renamed, moved to another AIM package, or rebuilt on a different blueprint,
 * this URL and the userscript must be updated together — otherwise the two
 * entry points launch different agents.
 */
export const AGENT_SPACES_BASE_URL =
  'https://agentspaces.amazon.dev/app/?mode=CUSTOM&aimPackageName=FusionLocalAgents&customAgentName=fusion-assistant&blueprint=AgentSpacesEmptyBlueprintProd&incognito=false';

/** Id of a runtime setup guide offered in the button's dropdown. */
export type FusionAssistantSetupGuideId = 'setup-kiro';

interface FusionAssistantSetupGuide {
  id: FusionAssistantSetupGuideId;
  text: string;
  href: string;
}

/**
 * Runtime setup guide offered in the button's dropdown. This is the single
 * "local setup path" entry point: the main action recommends the managed
 * AgentSpaces environment, while this guide links writers who prefer a local
 * editor (Kiro CLI or Claude Code) to the published Fusion user guide's
 * Local Setup Path — distinct from AgentSpaces. It points at the alpha docs
 * render, matching the other in-app Fusion documentation links (see Home.tsx).
 */
export const FUSION_ASSISTANT_SETUP_GUIDES: readonly FusionAssistantSetupGuide[] =
  [
    {
      id: 'setup-kiro',
      text: 'Add Fusion to your developer environment',
      href: 'https://alpha.www.docs.aws.a2z.com/fusion/latest/userguide/getting-started.html#setup-path-local',
    },
  ];

/**
 * Adoption-beacon endpoint, per environment. The alpha website reports to the
 * beta beacon and integ (production) reports to the prod beacon. The trailing
 * pipe is part of the path segment format the beacon expects:
 * <metric>|<event>|<page>/<alias>.
 */
const FUSION_ASSISTANT_BEACON_URL_BY_ENV = {
  alpha:
    'https://beta.us-west-2.tcx-beacon.docs.aws.dev/basic-beacon/TlcwsFusionAssistant|OPEN|',
  gamma:
    'https://beta.us-west-2.tcx-beacon.docs.aws.dev/basic-beacon/TlcwsFusionAssistant|OPEN|',
  integ:
    'https://prod.us-west-2.tcx-beacon.docs.aws.dev/basic-beacon/TlcwsFusionAssistant|OPEN|',
} as const;

/** The adoption-beacon endpoint for the current environment. */
export const getFusionAssistantBeaconUrl = (): string =>
  FUSION_ASSISTANT_BEACON_URL_BY_ENV[getEnvironment()];

/** Prompt used when the button carries no entity context. */
export const FUSION_ASSISTANT_PROMPT_GENERIC =
  "I'm working on conference content using FLC, start up the SOP/workflow with the discovery phase.";

/** Opening clause of a prompt that carries entity context. */
export const FUSION_ASSISTANT_PROMPT_PREFIX =
  "I'm working on conference content using FLC";

/** Opening clause for a cross-venue shared-package prompt. */
export const FUSION_ASSISTANT_PROMPT_CROSS_VENUE_PREFIX =
  'I have one or more packages shared across venues in FLC and need to coordinate cross-venue branch management';

/** Closing clause of a prompt that carries entity context. */
export const FUSION_ASSISTANT_PROMPT_SUFFIX =
  '. Start the SOP/workflow with the discovery phase.';
